import { Node, parse } from 'acorn';
import { buildUtilityFromKey } from '../../ai-adapter/utility/utility';

/**
 * The AI "Build the function" flow for an EXECUTE_ENTER_FUNCTION rule action (FE-only — no
 * extension-side change, see the plan). Everything here is pure and synchronous: composing the
 * prompt we send, and parsing what comes back. The actual model call is `aiBridge.chat` in
 * `worker.store.ts`.
 *
 * The function this produces is NOT saved, tested or executed by anything in this repo — it is
 * text a person reviews in the code editor before Save. `checkWorkerFunctionSyntax` only parses
 * it (acorn), never runs it.
 */

/** Markers the model is told to wrap its code in — cheap, unambiguous to extract, and already the
 *  pattern this codebase uses for structured AI output (see `ai-assistant.tsx`'s reply markers). */
const FUNCTION_START = '<<<FUNCTION_START>>>';
const FUNCTION_END = '<<<FUNCTION_END>>>';

/**
 * The wrapper the backend uses to execute the stored snippet. The snippet is inserted verbatim
 * inside this wrapper — it's NOT a function declaration, just statements that run in scope of
 * `payload`, `payers`, and `providers`. Syntax validation parses the snippet inside this same
 * wrapper so that `return`, `payload`, `payers`, and `providers` are valid.
 */
const BACKEND_WRAPPER_START = '(async function (payload, payers, providers) {\n';
const BACKEND_WRAPPER_END = '\n})';

// Field guide below is hand-curated from the generated API models (NS-10), not invented (NS-4).
// Typed against the real interfaces so a `tsc` run fails loudly if a field this cites is ever
// renamed or removed from the generated client — see the `satisfies` arrays at the bottom.
const TOP_LEVEL_FIELDS = [
    'claimNarrative — free-text narrative for the claim',
    "subscribers — Array<{ payerId, subscriberId?, memberId?, first?, middle?, last?, dob?, phone?, sex?, ssn?, addresses? }>; subscribers[0] is primary",
    'patient — { patientId?, first?, middle?, last?, dob?, sex?, phones?, emails?, addresses? }',
    'guarantor — { first?, middle?, last?, dob?, phone?, email?, organization? }',
    "providers — Array<{ providerId?, organization?, first?, middle?, last?, npi?, taxonomy?, type }>",
    'facility — { facilityId?, name?, npi?, ein?, addresses? }',
    'seller — { sellerId?, organization?, first?, middle?, last? }',
    'billingProviderId, chargemasterId, claimBundleId, controlNumber, cliaNumber — string ids',
    'primaryPayerAuthorizationNumber, secondaryPayerAuthorizationNumber, tertiaryPayerAuthorizationNumber — string',
    "primaryThirdPartyAdministratorPayerId, secondaryThirdPartyAdministratorPayerId, tertiaryThirdPartyAdministratorPayerId — string",
    'maximumGuarantorResponsibility — { amount: string, currency? } (see chargeAmount below — never a bare number)',
    'services — Array<service line>, see below',
];

const SERVICE_FIELDS = [
    'procedureCode — string, e.g. CPT "99213"',
    'procedureModifierCodes — string[], e.g. ["25"]',
    'diagnosisCodes — string[], e.g. ["M54.5"]',
    'units — number',
    'chargeAmount — { amount: string, currency? }; amount is a STRING like "150.00", never a number',
    'fromDateOfService, throughDateOfService — string dates',
    'placeOfService — string enum, e.g. "OFFICE" | "TELEHEALTH" | "HOME" | "URGENT_CARE_FACILITY"',
    'emergency — boolean',
    'narrative — string',
    'providers, facility — same shapes as the top-level fields above, service-line-specific overrides',
];

// Field guide for the `payers` argument — the account's payer directory.
const PAYERS_FIELDS = [
    'id — unique payer identifier (string)',
    'name — payer display name (string)',
];

// Field guide for the `providers` argument — the account's provider directory.
// Note: `payload.providers` is DIFFERENT — it's the providers on the claim, not the account directory.
const PROVIDERS_DIRECTORY_FIELDS = [
    'id — unique provider identifier (string)',
    'npi — National Provider Identification (string)',
    'organization — organization name (string, optional)',
    'first — individual first name (string, optional)',
    'last — individual last name (string, optional)',
];

export interface WorkerFunctionUtilityArgs {
    /** 'Support' | 'Provider' — which side is authoring this rule; forwarded to `buildUtilityFromKey`. */
    side: string;
}

/**
 * The utility string sent as `context` for the extension's model call (`AiRequest.utility`).
 * Layers the strict output contract on top of the page-purpose block every other AI surface
 * sends (`utility-contexts.json`'s `workerFunction` entry) — same pattern as `ai-assistant.tsx`'s
 * `suggestReply` task appending its own formatting instructions to the base page utility.
 */
export function buildWorkerFunctionUtility({ side }: WorkerFunctionUtilityArgs): string {
    const base = buildUtilityFromKey(`workerFunction:${side}`);

    const contract = [
        'STRICT OUTPUT CONTRACT:',
        '- Write ONLY the statements (the snippet) that run inside the function. Do NOT write a function declaration or wrap your code in a function — the backend already wraps it.',
        '- Your code runs inside: `(async function (payload, payers, providers) { <your code> })`',
        '- Three variables are in scope:',
        '  - `payload` — the claim-edit payload (described below). Mutate it as needed.',
        '  - `payers` — the account\'s payer directory (array), for looking up payer IDs by name.',
        '  - `providers` — the account\'s provider directory (array), for looking up provider IDs by NPI or name. Note: this is DIFFERENT from `payload.providers` which are the providers on the claim.',
        '- End with `return payload;` to apply your edits to the claim.',
        "- To stop processing this claim instead, `throw new Error('reason')` — do not return early with no explanation.",
        '- `payload` has already passed backend validation before this function runs. Do NOT add defensive checks against that — no `if (!payload)`, no `Array.isArray(...)`, no `.length === 0` guards on baseline fields like `payload.services` or `payload.subscribers`. That duplicates validation the backend already did and is code nobody asked for.',
        '- Only branch on a condition when the user’s instructions actually describe one — e.g. "if a same-day procedure line exists" or "skip claims that already have modifier 25" is the requested business logic, not payload validation. Use `.find()` / `.some()` for that, and only handle a "not found" case if the user said what should happen then.',
        '- Change ONLY what the user asked for. Do not add unrelated validation, logging, or error handling.',
        '- Plain synchronous JavaScript that touches ONLY `payload`, `payers`, and `providers`. Never write any of the following, under any name or indirection: `fetch`, `XMLHttpRequest`, `WebSocket`, `eval`, the `Function` constructor, `require`, `import`, `process`, `global`, `globalThis`, `window`, `document`, `console.*`, `setTimeout`/`setInterval`, or `async`/`await`.',
        '- No unbounded loops — `while (true)`, `for (;;)`, or any loop whose exit condition is not guaranteed to be reached. Iterate over arrays with `.forEach()` / `.map()` / `.filter()` / `.find()` / a bounded `for` over a known-length array instead.',
        '',
        'PAYLOAD SHAPE (the claim, `payload`):',
        ...TOP_LEVEL_FIELDS.map(line => `  ${line}`),
        '',
        'PAYLOAD SHAPE (each entry of `payload.services`):',
        ...SERVICE_FIELDS.map(line => `  ${line}`),
        '',
        'PAYERS DIRECTORY (`payers` array, each entry):',
        ...PAYERS_FIELDS.map(line => `  ${line}`),
        '',
        'PROVIDERS DIRECTORY (`providers` array, each entry — NOT the same as `payload.providers`):',
        ...PROVIDERS_DIRECTORY_FIELDS.map(line => `  ${line}`),
        '',
        `Wrap your code between ${FUNCTION_START} and ${FUNCTION_END} on their own lines, exactly once.`,
        'You may add one short sentence explaining what you wrote OUTSIDE the markers — nothing else outside them.',
    ];

    return `${base}\n\n${contract.join('\n')}`;
}

export interface WorkerFunctionRuleContext {
    /** What the rule's WHEN conditions match on, e.g. "Payment: CARC CO · code 97" — already formatted. */
    matchSummary: string;
    ruleName: string;
    /** 'denial' | 'rejection' automation worker id. */
    workerId: string;
}

/** Context JSON for the request — what the rule fires on, so the model can tailor guards to it. */
export function buildWorkerFunctionContextJson(context: WorkerFunctionRuleContext): string {
    return JSON.stringify(context);
}

const FENCED_CODE_BLOCK = /```(?:js|javascript)?\s*\n?([\s\S]*?)```/i;

// Patterns for unwrapping function wrappers the model might include despite being told not to.
// Plain function: `function name(args) { body }`
const PLAIN_FUNCTION_WRAPPER = /^\s*function\s+\w+\s*\([^)]*\)\s*\{\s*([\s\S]*?)\s*\}\s*;?\s*$/;
// Async function: `async function name(args) { body }` or `async function (args) { body }`
const ASYNC_FUNCTION_WRAPPER = /^\s*(?:\(?\s*async\s+function\s*\w*\s*\([^)]*\)\s*\{\s*([\s\S]*?)\s*\}\s*\)?)\s*;?\s*$/;

/**
 * Pulls the function body out of the model's reply. Tries the markers first (the contract we
 * asked for); falls back to a fenced code block, since models drift from instructions. Returns
 * `null` — never an empty string — when nothing usable was found, so the caller can tell "no code"
 * from "empty function" (`getExecuteFunctionValidationError` already means the latter).
 */
export function extractGeneratedFunction(replyText: string): string | null {
    const markerMatch = replyText.match(
        new RegExp(`${FUNCTION_START}\\s*\\n?([\\s\\S]*?)\\n?\\s*${FUNCTION_END}`),
    );
    let body = markerMatch?.[1];

    if (body === undefined) {
        const fenced = replyText.match(FENCED_CODE_BLOCK);
        body = fenced?.[1];
    }

    if (body === undefined) {
        return null;
    }

    const trimmed = body.trim();
    if (!trimmed) {
        return null;
    }

    // If the model included a function wrapper despite being told not to, strip it back to a body.
    // Check async wrapper first (more specific), then plain function wrapper.
    const asyncWrapped = trimmed.match(ASYNC_FUNCTION_WRAPPER);
    if (asyncWrapped) {
        return asyncWrapped[1].trim();
    }

    const plainWrapped = trimmed.match(PLAIN_FUNCTION_WRAPPER);
    if (plainWrapped) {
        return plainWrapped[1].trim();
    }

    return trimmed;
}

/**
 * Syntax check only — NEVER executes the code (no `eval`/`new Function` call anywhere in this
 * file). Wraps as a function body using the same wrapper the backend uses, so `return`, `payload`,
 * `payers`, and `providers` are all valid in the snippet.
 */
export function checkWorkerFunctionSyntax(code: string): string | null {
    if (!code.trim()) {
        return null;
    }

    try {
        parse(`${BACKEND_WRAPPER_START}${code}${BACKEND_WRAPPER_END}`, { ecmaVersion: 'latest' });
        return null;
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Syntax error.';
        const stripped = message.replace(/\s*\(\d+:\d+\)\s*$/, '');
        // The editor has no inline linter, so the position has to be in the message. Acorn counts
        // lines in the wrapped source, whose first line is the wrapper header — the snippet starts
        // on line 2 of it.
        const loc = (error as { loc?: { line: number; column: number } }).loc;
        return loc ? `${stripped} (line ${loc.line - 1}, column ${loc.column + 1})` : stripped;
    }
}

/**
 * NOT a sandbox and not a security boundary (PR review, 2026-09-28) — this is a best-effort,
 * static check against direct, unobfuscated use of a fixed name list, the same limitation the
 * AI prompt's instructions already have. `payload['fe' + 'tch']`, reaching a global through some
 * other property chain, or any other indirection walks straight past it. Real enforcement has to
 * live at the execution boundary (the backend), which this repo cannot reach or verify (NS-12).
 * This exists to catch the direct, common case — including a hand-typed line, not just AI output —
 * before Save, not to make the feature safe to run unobserved.
 */
const FORBIDDEN_GLOBAL_NAMES = new Set([
    'fetch',
    'XMLHttpRequest',
    'WebSocket',
    'eval',
    'Function',
    'require',
    'process',
    'global',
    'globalThis',
    'window',
    'document',
    'console',
    'setTimeout',
    'setInterval',
    'setImmediate',
]);

type AstNode = Node & Record<string, unknown>;

/**
 * A loop test that provably never becomes false without evaluating anything at runtime — only a
 * literal (`true`, `1`, `"x"`, …), judged by ordinary JS truthiness. Deliberately narrow: no
 * constant folding of expressions like `!false` or `1 + 1`, since a false NEGATIVE here (missing
 * a truthy test written some other way) is the safe direction to be wrong in for a check whose
 * false POSITIVE would block a legitimate loop from saving at all.
 */
function isStaticallyTruthyTest(node: AstNode | null | undefined): boolean {
    if (!node || node.type !== 'Literal') {
        return false;
    }
    return Boolean((node as unknown as { value: unknown }).value);
}

/** Generic ESTree walk: acorn nodes are plain nested objects/arrays, keyed by whatever each node
 *  type happens to carry — no separate walker package needed for a check this narrow. Threads the
 *  immediate parent's type/key/computed-ness down to `visit` so it can tell a value REFERENCE
 *  apart from a binding, a property key, or a label — see `isNonReferenceIdentifierPosition`. */
function walkAst(
    value: unknown,
    visit: (node: AstNode, parentType: string | null, key: string | null, parentComputed: boolean) => void,
    parentType: string | null = null,
    key: string | null = null,
    parentComputed = false,
): void {
    if (!value || typeof value !== 'object') {
        return;
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            walkAst(item, visit, parentType, key, parentComputed);
        }
        return;
    }
    const node = value as AstNode;
    if (typeof node.type === 'string') {
        visit(node, parentType, key, parentComputed);
    }
    const computed = node.computed === true;
    for (const childKey of Object.keys(node)) {
        if (childKey === 'type' || childKey === 'start' || childKey === 'end' || childKey === 'loc' || childKey === 'range') {
            continue;
        }
        walkAst(node[childKey], visit, (node.type as string) ?? null, childKey, computed);
    }
}

/**
 * True for every ESTree position where an `Identifier` node is a BINDING, a plain KEY, or a
 * LABEL — never a read of the name's value — so `checkWorkerFunctionForbiddenPatterns` can tell
 * `const fetch = 1;` (declares a harmless local, never checked) from `const fn = fetch;` (reads
 * the real global, must be checked) using only immediate parent context, no full scope analysis.
 *
 * Known, accepted trade-off in the OTHER direction: a destructuring binding that happens to share
 * a forbidden name (`const { window } = payload;`) is NOT excluded here, so it gets (incorrectly)
 * flagged as if it read the global. That is a false positive — the safe direction to be wrong in
 * for a Save-blocking check — and `payload` has no field named after any entry in
 * `FORBIDDEN_GLOBAL_NAMES` (see the field guide above), so it is not expected to come up for real.
 */
function isNonReferenceIdentifierPosition(parentType: string, key: string, parentComputed: boolean): boolean {
    if (parentType === 'Property' && key === 'key' && !parentComputed) {
        return true; // `{ window: 1 }` — the key name, not a read of `window`.
    }
    if (parentType === 'MemberExpression' && key === 'property' && !parentComputed) {
        return true; // `payload.window` — a field name, not a read of `window`.
    }
    if (parentType === 'VariableDeclarator' && key === 'id') {
        return true; // `const fetch = 1;` — declares a local, never a read.
    }
    if (
        (parentType === 'FunctionDeclaration' ||
            parentType === 'FunctionExpression' ||
            parentType === 'ClassDeclaration' ||
            parentType === 'ClassExpression') &&
        key === 'id'
    ) {
        return true; // a function/class named `fetch` — its own declared name, not a call to it.
    }
    if (parentType === 'CatchClause' && key === 'param') {
        return true; // `catch (window) {}` — the caught error's local name.
    }
    if (
        (parentType === 'FunctionDeclaration' ||
            parentType === 'FunctionExpression' ||
            parentType === 'ArrowFunctionExpression') &&
        key === 'params'
    ) {
        return true; // `function f(window) {}` — a parameter name.
    }
    if (parentType === 'AssignmentPattern' && key === 'left') {
        return true; // `function f(window = 1) {}` — still the parameter name, not the default value.
    }
    if (
        (parentType === 'LabeledStatement' || parentType === 'BreakStatement' || parentType === 'ContinueStatement') &&
        key === 'label'
    ) {
        return true; // loop labels live in a separate namespace from variables entirely.
    }
    return false;
}

/**
 * Scope-aware only to the extent above — a per-node parent check, not full binding resolution.
 * `this.fetch(...)` (a sloppy-mode implicit-global access via `this`, not an `Identifier` at all)
 * is a known, still-open gap alongside the aliasing one already documented on the exported
 * function below; neither is closed by this pass.
 */
function checkForbiddenIdentifier(node: AstNode, parentType: string | null, key: string | null, parentComputed: boolean): string | null {
    if (node.type !== 'Identifier') {
        return null;
    }
    const name = node.name as string;
    if (!FORBIDDEN_GLOBAL_NAMES.has(name)) {
        return null;
    }
    if (parentType !== null && key !== null && isNonReferenceIdentifierPosition(parentType, key, parentComputed)) {
        return null;
    }
    return name;
}

/**
 * Checks if the snippet is ONLY a function declaration with nothing else — this is wrong because
 * the backend wraps the snippet in `(async function (payload, payers, providers) { ... })`, so
 * a standalone `function foo() { ... }` would define a function that is never called.
 */
function isOnlyFunctionDeclaration(code: string): boolean {
    let program: AstNode;
    try {
        program = parse(`${BACKEND_WRAPPER_START}${code}${BACKEND_WRAPPER_END}`, { ecmaVersion: 'latest' }) as unknown as AstNode;
    } catch {
        return false;
    }

    // The AST is: Program > ExpressionStatement > FunctionExpression (wrapper) > body.body = [statements]
    const wrapper = (program.body as AstNode[])?.[0];
    const wrapperExpr = wrapper?.expression as AstNode | undefined;
    const body = wrapperExpr?.body as AstNode | undefined;
    const statements = body?.body as AstNode[] | undefined;

    if (!statements || statements.length !== 1) {
        return false;
    }

    // The entire snippet is one FunctionDeclaration and nothing else
    return statements[0].type === 'FunctionDeclaration';
}

/**
 * Flags a REFERENCE to a forbidden name wherever one can appear as a value — a call (e.g. calling
 * `fetch`), `new` (`new WebSocket(...)`), member access (`window.foo`, `window['foo']`), a bare assignment
 * (`const fn = fetch;` — the exact gap a PR review caught on 2026-09-28), a return value, an
 * argument passed to something else, and so on — by checking every `Identifier` node against its
 * immediate parent position rather than special-casing each syntactic shape separately.
 */
export function checkWorkerFunctionForbiddenPatterns(code: string): string | null {
    if (!code.trim()) {
        return null;
    }

    // Check if the entire snippet is just a function declaration — the backend wraps snippets in
    // an async function, so defining a function without calling it does nothing useful.
    if (isOnlyFunctionDeclaration(code)) {
        return 'Remove the function wrapper — write only the statements that run inside the function, not a function declaration. The backend already wraps your code.';
    }

    let program: AstNode;
    try {
        program = parse(`${BACKEND_WRAPPER_START}${code}${BACKEND_WRAPPER_END}`, { ecmaVersion: 'latest' }) as unknown as AstNode;
    } catch {
        // Not this function's job — checkWorkerFunctionSyntax reports parse errors.
        return null;
    }

    let found: string | null = null;
    const report = (name: string): void => {
        found = found ?? name;
    };

    // Track the wrapper function so we don't flag its `async` as a violation.
    // The wrapper is the outermost FunctionExpression in the parsed AST.
    let wrapperNode: AstNode | null = null;
    const wrapper = (program.body as AstNode[])?.[0];
    const wrapperExpr = wrapper?.expression as AstNode | undefined;
    if (wrapperExpr?.type === 'FunctionExpression') {
        wrapperNode = wrapperExpr;
    }

    walkAst(program, (node, parentType, key, parentComputed) => {
        if (found) {
            return;
        }

        const identifierHit = checkForbiddenIdentifier(node, parentType, key, parentComputed);
        if (identifierHit) {
            report(identifierHit);
            return;
        }

        switch (node.type) {
            case 'CallExpression':
            case 'NewExpression': {
                // `import(...)`'s callee has its own node type, not an Identifier — the general
                // check above cannot see it, so it needs this one explicit case.
                const callee = node.callee as AstNode | undefined;
                if (callee?.type === 'Import') {
                    report('import');
                }
                break;
            }
            case 'ImportExpression':
                report('import');
                break;
            case 'AwaitExpression':
                report('await');
                break;
            case 'WhileStatement':
                if (isStaticallyTruthyTest(node.test as AstNode | undefined)) {
                    report('while (true)');
                }
                break;
            case 'DoWhileStatement':
                if (isStaticallyTruthyTest(node.test as AstNode | undefined)) {
                    report('do…while (true)');
                }
                break;
            case 'ForStatement':
                // `node.test === null` is `for (;;)` — no test at all, not just a falsy one.
                if (node.test === null || isStaticallyTruthyTest(node.test as AstNode | undefined)) {
                    report('for (;;)');
                }
                break;
            default:
                break;
        }

        // Check for async functions, but SKIP the wrapper function itself — its async is the
        // backend's requirement, not a violation. Only user-defined async functions are forbidden.
        if (
            node.async === true &&
            node !== wrapperNode &&
            (node.type === 'FunctionDeclaration' ||
                node.type === 'FunctionExpression' ||
                node.type === 'ArrowFunctionExpression')
        ) {
            report('async function');
        }
    });

    if (!found) {
        return null;
    }

    if (found === 'while (true)' || found === 'do…while (true)' || found === 'for (;;)') {
        return `Remove the unbounded loop (\`${found}\`) — every loop must be guaranteed to end.`;
    }
    if (found === 'async function' || found === 'await') {
        return "Remove `async`/`await` — this function must be plain synchronous JavaScript.";
    }
    if (found === 'import') {
        return 'Remove the dynamic `import(...)` — nothing may be loaded from outside `payload`.';
    }
    return `Remove \`${found}\` — this function may only read and write \`payload\`, \`payers\`, and \`providers\`.`;
}

